import React from 'react';
import { Avatar, Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HotelIcon from '@mui/icons-material/Hotel';
import MaintenanceRoomIndicator from '../housekeeping/MaintenanceRoomIndicator';
import { getMaintenanceCardAccentSx, resolveRoomMaintenanceSummary } from '../../utils/maintenanceRoomUtils';
import {
  getCleanStatusMeta,
  getUltraRoomAvatarSx,
  getUltraRoomBodyChipSx,
  getUltraRoomCardBodySx,
  getUltraRoomCardFooterSx,
  getUltraRoomCardSx,
  getUltraRoomCardTextSx,
  getUltraRoomHeroSx,
  getUltraRoomMetaSx,
  getUltraRoomNumberSx,
  getUltraRoomStatusChipSx,
  ULTRA_GRID_ACCENT,
} from '../../utils/dashboardUltraGridLayout';
import {
  getRoomPickerSelectedImageCheckSx,
  getRoomPickerSelectedImageOverlaySx,
} from '../../utils/roomPickerLayout';

const HOLD_COLORS = {
  web_checkin: ULTRA_GRID_ACCENT,
  pending_checkout: '#fb923c',
  overdue_checkout: '#f43f5e',
  maintenance: '#94a3b8',
};

function getPickerColors(isDark, { isSelected, walkInHeld, heldKind, cleanColor }) {
  const holdColor = HOLD_COLORS[heldKind] || '#fb923c';
  const dot = isSelected ? ULTRA_GRID_ACCENT : (walkInHeld ? holdColor : cleanColor);
  return {
    dot,
    border: isDark ? alpha(dot, 0.3) : alpha(dot, 0.22),
    shadow: isDark ? '0 6px 18px rgba(0,0,0,0.32)' : '0 2px 8px rgba(15,23,42,0.06)',
    heroGlow: walkInHeld || isSelected ? 0.16 : 0.08,
  };
}

export default function RoomPickerGridCard({
  room,
  isDark = true,
  isMobile = false,
  isSelected = false,
  isDisabled = false,
  walkInHeld = false,
  heldLabel = 'Pending checkout',
  heldKind = 'pending_checkout',
  heldTooltip = '',
  imageUrl = '',
  onClick,
}) {
  const cleanMeta = getCleanStatusMeta(room?.clean_status || 'clean');
  const maintenanceSummary = resolveRoomMaintenanceSummary(room);
  const disabled = isDisabled || walkInHeld;
  const colors = getPickerColors(isDark, {
    isSelected,
    walkInHeld,
    heldKind,
    cleanColor: cleanMeta.color,
  });
  const tooltipTitle = walkInHeld
    ? (heldTooltip || heldLabel)
    : (isDisabled ? heldTooltip : '');

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow describeChild>
      <Paper
        elevation={0}
        onClick={onClick}
        sx={[
          getUltraRoomCardSx(isDark, isMobile, colors, {
            showAsDisabled: disabled,
            clickable: !disabled,
            isWorkspace: true,
          }),
          { minHeight: isMobile ? 148 : 160 },
          isSelected
            ? {
                border: `1.5px solid ${alpha(ULTRA_GRID_ACCENT, isDark ? 0.55 : 0.42)}`,
                boxShadow: isDark
                  ? `0 0 0 1px ${alpha(ULTRA_GRID_ACCENT, 0.22)}, 0 10px 24px ${alpha('#000', 0.35)}`
                  : `0 0 0 1px ${alpha(ULTRA_GRID_ACCENT, 0.18)}, 0 8px 18px ${alpha(ULTRA_GRID_ACCENT, 0.12)}`,
                '&::before': {
                  background: `linear-gradient(90deg, ${alpha(ULTRA_GRID_ACCENT, 0.2)} 0%, ${ULTRA_GRID_ACCENT} 50%, ${alpha(ULTRA_GRID_ACCENT, 0.2)} 100%)`,
                },
              }
            : null,
          maintenanceSummary.hasMaintenance
            ? getMaintenanceCardAccentSx(maintenanceSummary.priority, isDark)
            : null,
        ]}
      >
        <Box sx={getUltraRoomHeroSx(isMobile, isDark, colors)}>
          <Chip
            size="small"
            label={cleanMeta.label}
            sx={getUltraRoomStatusChipSx(cleanMeta.color, isDark)}
          />
          {maintenanceSummary.hasMaintenance ? (
            <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}>
              <MaintenanceRoomIndicator room={room} isDark={isDark} variant="icon" compact />
            </Box>
          ) : null}

          <Box sx={{ position: 'relative', zIndex: 1, mt: 0.45 }}>
            <Avatar
              src={imageUrl || undefined}
              alt=""
              sx={getUltraRoomAvatarSx(isMobile, isDark, colors)}
            >
              <HotelIcon sx={{ fontSize: isMobile ? 22 : 26 }} />
            </Avatar>
            {isSelected ? (
              <Box sx={getRoomPickerSelectedImageOverlaySx(isMobile, isDark)} aria-hidden>
                <CheckCircleIcon sx={getRoomPickerSelectedImageCheckSx(isMobile)} />
              </Box>
            ) : null}
          </Box>

          <Typography component="div" sx={getUltraRoomNumberSx(isMobile, isDark, colors)}>
            {room.room_number}
          </Typography>
        </Box>

        <Box sx={getUltraRoomCardBodySx(isDark, isMobile, colors)}>
          <Box sx={getUltraRoomCardTextSx(isMobile)}>
            <Typography sx={getUltraRoomMetaSx(isDark)}>
              {room.floor ? `Floor ${room.floor}` : '—'}
            </Typography>
          </Box>
          <Box sx={getUltraRoomCardFooterSx(isMobile)}>
            <Chip size="small" label={cleanMeta.label} sx={getUltraRoomBodyChipSx(cleanMeta.color, isDark)} />
            {walkInHeld ? (
              <Chip size="small" label={heldLabel} sx={getUltraRoomBodyChipSx(colors.dot, isDark)} />
            ) : null}
          </Box>
        </Box>
      </Paper>
    </Tooltip>
  );
}
